// Fixed sidebar (259px) — WhatChimp design system parity (WP-00).
// Responsive behavior:
//   ≥1024px (lg): full 259px white sidebar with coloured icons & labels.
//   768–1023px (md): collapsed 72px icon rail (labels hidden).
//   <768px: off-canvas drawer toggled by the topbar burger.
// Logical utilities only (ms-/me-/border-e/start-) so it mirrors under RTL.
import { NavLink } from 'react-router-dom';
import { useI18n } from '../../lib/i18n';
import { cn } from '../../lib/cn';
import { NAV_ITEMS } from '../../lib/nav';

export default function Sidebar({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const { t } = useI18n();

  return (
    <aside
      className={cn(
        'fixed inset-y-0 start-0 z-40 flex w-[259px] flex-col overflow-y-auto border-e border-line bg-white p-3 transition-transform duration-200',
        'dark:border-slate-800 dark:bg-slate-900',
        'md:sticky md:top-0 md:h-screen md:w-[72px] md:translate-x-0 md:rtl:translate-x-0 lg:w-[259px]',
        open ? 'translate-x-0' : '-translate-x-full rtl:translate-x-full',
      )}
      aria-label={t('nav.dashboard')}
    >
      <div className="mb-4 flex items-center gap-2.5 px-2.5 py-2 md:justify-center lg:justify-start">
        <img src="/watag-mark.svg" alt="WaTag" className="h-8 w-8 shrink-0" />
        <span className="text-[16px] font-bold tracking-tight text-slate-800 dark:text-slate-100 md:hidden lg:inline">
          {t('app.name')}
        </span>
      </div>

      <nav className="flex flex-col gap-0.5">
        {NAV_ITEMS.map((item) => {
          const Icon = item.icon;
          return (
            <NavLink
              key={item.path}
              to={item.path}
              end={item.path === '/'}
              onClick={onClose}
              title={t(item.key)}
              className={({ isActive }) =>
                cn(
                  'flex items-center gap-3 rounded-lg px-3 py-2.5 text-[14px] font-medium text-slate-600 transition-colors',
                  'hover:bg-slate-50 hover:text-slate-900 dark:text-slate-300 dark:hover:bg-slate-800 dark:hover:text-white',
                  'md:justify-center lg:justify-start',
                  isActive &&
                    'bg-brand-50/70 font-semibold text-brand hover:bg-brand-50/70 hover:text-brand dark:bg-slate-800 dark:text-brand-tint',
                )
              }
            >
              <span className="flex shrink-0 items-center justify-center" aria-hidden>
                <Icon size={18} className={cn('shrink-0', item.color || 'text-slate-500')} />
              </span>
              <span className="truncate md:hidden lg:inline">{t(item.key)}</span>
              {item.locked && (
                <span className="ms-auto rounded-full bg-amber-50 px-1.5 py-0.5 text-[10px] font-bold text-amber-600 dark:bg-amber-950/40 dark:text-amber-400 md:hidden lg:inline">
                  {t('common.soon')}
                </span>
              )}
            </NavLink>
          );
        })}
      </nav>
    </aside>
  );
}
